import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SyncScheduler = ({ schedules, onUpdateSchedule, onCreateSchedule }) => {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    connector: '',
    frequency: 'daily',
    time: '02:00',
    notifications: true,
    enabled: true
  });

  const frequencyOptions = [
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const connectorOptions = [
    { value: 'sap', label: 'SAP SuccessFactors' },
    { value: 'workday', label: 'Workday HCM' },
    { value: 'bamboohr', label: 'BambooHR' }
  ];

  const handleCreateSchedule = async () => {
    await onCreateSchedule(newSchedule);
    setNewSchedule({
      name: '',
      connector: '',
      frequency: 'daily',
      time: '02:00',
      notifications: true,
      enabled: true
    });
    setIsCreating(false);
  };

  const getFrequencyIcon = (frequency) => {
    switch (frequency) {
      case 'hourly': return 'Clock';
      case 'daily': return 'Calendar';
      case 'weekly': return 'CalendarDays';
      case 'monthly': return 'CalendarRange';
      default: return 'Clock';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">{t('dataIntegration.sync.sectionTitle')}</h3>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setIsCreating(true)}
        >
          New Schedule
        </Button>
      </div>
      {isCreating && (
        <div className="bg-muted rounded-lg p-4 mb-6 space-y-4">
          <h4 className="font-medium text-foreground">{t('dataIntegration.sync.createNew')}</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Schedule Name
              </label>
              <input
                type="text"
                value={newSchedule?.name}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder={t('dataIntegration.sync.scheduleNamePlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Connector
              </label>
              <Select
                options={connectorOptions}
                value={newSchedule?.connector}
                onChange={(value) => setNewSchedule(prev => ({ ...prev, connector: value }))}
                placeholder={t('dataIntegration.sync.selectConnector')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Frequency
              </label>
              <Select
                options={frequencyOptions}
                value={newSchedule?.frequency}
                onChange={(value) => setNewSchedule(prev => ({ ...prev, frequency: value }))}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Time
              </label>
              <input
                type="time"
                value={newSchedule?.time}
                onChange={(e) => setNewSchedule(prev => ({ ...prev, time: e?.target?.value }))}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Checkbox
              label={t('dataIntegration.sync.enableEmail')}
              checked={newSchedule?.notifications}
              onChange={(e) => setNewSchedule(prev => ({ ...prev, notifications: e?.target?.checked }))}
            />
            <Checkbox
              label={t('dataIntegration.sync.enableSchedule')}
              checked={newSchedule?.enabled}
              onChange={(e) => setNewSchedule(prev => ({ ...prev, enabled: e?.target?.checked }))}
            />
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="default"
              iconName="Check"
              iconPosition="left"
              onClick={handleCreateSchedule}
            >
              Create Schedule
            </Button>
            <Button
              variant="outline"
              iconName="X"
              iconPosition="left"
              onClick={() => setIsCreating(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {schedules?.map((schedule) => (
          <div key={schedule?.id} className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-background rounded-lg flex items-center justify-center">
                <Icon name={getFrequencyIcon(schedule?.frequency)} size={20} className="text-muted-foreground" />
              </div>
              
              <div>
                <div className="flex items-center space-x-2">
                  <h4 className="font-medium text-foreground">{schedule?.name}</h4>
                  {schedule?.enabled ? (
                    <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-muted-foreground/10 text-muted-foreground text-xs rounded-full">
                      Inactive
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {schedule?.connector} • {schedule?.frequency} at {schedule?.time}
                </p>
                <p className="text-xs text-muted-foreground">
                  Next run: {schedule?.nextRun} • Last run: {schedule?.lastRun}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                iconName="Play"
                iconPosition="left"
              >
                Run Now
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Settings"
                iconPosition="left"
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName={schedule?.enabled ? 'Pause' : 'Play'}
                onClick={() => onUpdateSchedule(schedule?.id, { enabled: !schedule?.enabled })}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SyncScheduler;